import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@carepass/api-client";
import { memberSessionStore } from "../../app/runtime";
import { memberApi } from "../shared/api";
import { ErrorState, LoadingState, navigate } from "../shared/ui";
import { TerminationForm, type TerminationRequest } from "./TerminationForm";
import { TerminationResultView, TerminationScreen } from "./TerminationView";
import { saveTerminationResultSession } from "./termination-result-session";

export function TerminationPage() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string>();
  const [quoteReview, setQuoteReview] = useState<"refreshing" | "updated" | "error">();
  const [confirmationRevision, setConfirmationRevision] = useState(0);
  const submitting = useRef(false);
  const existing = useQuery({ queryKey: ["member", "termination"], queryFn: memberApi.termination, retry: false });
  const noExisting = existing.error instanceof ApiError && existing.error.kind === "not-found";
  const newRequest = !existing.isPending && !existing.data && (noExisting || !existing.isError);
  const preview = useQuery({ queryKey: ["member", "termination-preview"], queryFn: memberApi.terminationPreview, enabled: newRequest, retry: false, refetchOnWindowFocus: false });
  const needsAccount = newRequest && preview.data?.allowed && (preview.data.requiresBankAccount ?? (preview.data.bankRefundAmount ?? 0) > 0);
  const banks = useQuery({ queryKey: ["member", "banks"], queryFn: memberApi.banks, enabled: Boolean(needsAccount) });
  const profile = useQuery({ queryKey: ["member", "profile"], queryFn: memberApi.profile, enabled: Boolean(needsAccount) });
  const refreshPreview = async () => {
    setError(undefined);
    setConfirmationRevision(value => value + 1);
    setQuoteReview("refreshing");
    const result = await preview.refetch();
    setQuoteReview(result.error ? "error" : "updated");
  };
  const terminate = useMutation({
    mutationFn: (request: TerminationRequest) => memberApi.requestTermination(request),
    onSuccess: receipt => {
      saveTerminationResultSession(receipt.resultAccessToken, receipt.resultAccessExpiresAt);
      queryClient.removeQueries({ queryKey: ["termination-result"] });
      queryClient.setQueryData(["termination-result", receipt.resultAccessExpiresAt], receipt.refund);
      navigate("/termination/result", true);
      memberSessionStore.clear();
      queryClient.removeQueries({ queryKey: ["member"] });
    },
    onError: cause => {
      if (cause instanceof ApiError && ["TERMINATION_QUOTE_CHANGED", "TERMINATION_ACTIVE_TRANSACTION", "TERMINATION_NOT_ALLOWED"].includes(cause.code)) {
        void refreshPreview();
      } else setError(cause instanceof Error ? cause.message : "요청 결과를 확인하지 못했습니다. 다시 확인해 주세요.");
    },
    onSettled: () => { submitting.current = false; },
  });
  let content;
  if (existing.isPending) content = <div className="termination-loading"><LoadingState label="해지 정보를 확인하고 있습니다."/></div>;
  else if (existing.data) content = <TerminationResultView refund={existing.data} refreshing={existing.isFetching} onRefresh={() => void existing.refetch()}/>;
  else if (existing.isError && !noExisting) content = <ErrorState error={existing.error} onRetry={() => void existing.refetch()}/>;
  else if (preview.isPending) content = <div className="termination-loading"><LoadingState label="취소·환불 금액을 확인하고 있습니다."/></div>;
  else if (preview.isError && !preview.data) content = <ErrorState error={preview.error} onRetry={() => void preview.refetch()}/>;
  else content = <TerminationForm preview={preview.data} banks={banks.data ?? []} memberName={profile.data?.name ?? ""} pending={terminate.isPending} error={error}
    accountStatus={needsAccount && (banks.isPending || profile.isPending) ? "loading" : banks.isError || profile.isError ? "error" : "ready"}
    onRetryAccount={() => { void banks.refetch(); void profile.refetch(); }}
    quoteReview={quoteReview} confirmationRevision={confirmationRevision} onRetryPreview={() => void refreshPreview()}
    onSubmit={request => { if (submitting.current) return; submitting.current = true; setError(undefined); terminate.mutate(request); }}/>
  return <TerminationScreen pending={terminate.isPending} onClose={() => navigate(existing.data ? "/" : "/app/my")}>{content}</TerminationScreen>;
}
